import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import '../../labShared.css';
import './intro.css';

const BLUE_SATIN_STOPS = [
  ['0%', '#0F2B55'],
  ['20%', '#173B70'],
  ['43%', '#1B4584'],
  ['62%', '#3F6F9F'],
  ['76%', '#9FB9D0'],
  ['84%', '#DCE7F0'],
  ['91%', '#5A7FA7'],
  ['100%', '#183A6C'],
];

/** One outward pass on the wait star. Fixed logos pick a shorter duration of their own. */
const WAIT_LIGHT_TIMING = {
  dur: '1.8s',
  begin: 'indefinite',
  repeatCount: '1',
  fill: 'freeze',
  keyTimes: '0;0.28;0.52;0.75;1',
  calcMode: 'spline',
  keySplines: '0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1',
};

function motionAllowed() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Wait overlay: 0.5s through 3s, in 0.25s steps. */
function nextPulseGapMs() {
  const stepCount = Math.round((3000 - 500) / 250);
  return 500 + Math.floor(Math.random() * (stepCount + 1)) * 250;
}

/** Fixed logos: any pause from 1s through 7s between arm-light passes. */
function nextShimmerGapMs() {
  return 1000 + Math.floor(Math.random() * 6001);
}

/** Fixed logos: each pass lasts 0.12s through 0.36s. */
function nextShimmerDurMs() {
  const min = 120;
  const max = 360;
  return min + Math.floor(Math.random() * (max - min + 1));
}

function randomGradientStop() {
  return `${Math.round(-30 + Math.random() * 160)}%`;
}

/** New route each pulse. The first stop is where the light already rests, so it does not jump. */
function rerollWingLight(node) {
  const attr = node.getAttribute('attributeName');
  if (attr !== 'x1' && attr !== 'y1') return;
  const current = (node.getAttribute('values') || '').split(';').filter(Boolean);
  const start = current[current.length - 1] || randomGradientStop();
  const stops = [start];
  for (let index = 0; index < 4; index += 1) stops.push(randomGradientStop());
  node.setAttribute('values', stops.join(';'));
}

function LightSweep({ active, xValues, yValues }) {
  if (!active) return null;
  return (
    <>
      <animate attributeName="x1" values={xValues} {...WAIT_LIGHT_TIMING} />
      <animate attributeName="y1" values={yValues} {...WAIT_LIGHT_TIMING} />
    </>
  );
}

LightSweep.propTypes = {
  active: PropTypes.bool,
  xValues: PropTypes.string.isRequired,
  yValues: PropTypes.string.isRequired,
};

function BlueSatinGradient({ id, x1, y1, x2, y2, sweep = false }) {
  return (
    <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
      {BLUE_SATIN_STOPS.map(([offset, color]) => (
        <stop key={`${id}-${offset}`} offset={offset} stopColor={color} />
      ))}
      <LightSweep active={sweep} xValues="-40%;78%;-8%;82%;-40%" yValues="100%;18%;82%;12%;100%" />
    </linearGradient>
  );
}

BlueSatinGradient.propTypes = {
  id: PropTypes.string.isRequired,
  x1: PropTypes.string.isRequired,
  y1: PropTypes.string.isRequired,
  x2: PropTypes.string.isRequired,
  y2: PropTypes.string.isRequired,
  sweep: PropTypes.bool,
};

/**
 * Flash 13's gloss star. The blue halves use the satin gradient: navy,
 * a pale band, then navy again at the tip. Gold is unchanged.
 */
function Flash14StarMark({
  animated,
  ascent,
  wait = false,
  shimmer = false,
  decorative = false,
  size = '100%',
  heroSlot = false,
  label = 'FusionMSI',
}) {
  const uid = useId().replace(/:/g, '');
  const svgRef = useRef(null);
  const gapTimerRef = useRef(0);
  const travel = wait && motionAllowed();
  const sweep = (wait || shimmer) && motionAllowed();
  const [intro, setIntro] = useState(travel);
  const [pulsing, setPulsing] = useState(false);
  const rootClass = [
    'aperture-lab',
    'aperture-lab-flash14',
    animated && 'aperture-lab-flash14--animated',
    ascent && 'aperture-lab-flash14--ascent',
    wait && 'aperture-lab-flash14--wait',
    wait && intro && 'aperture-lab-flash14--intro',
    wait && pulsing && 'aperture-lab-flash14--pulsing',
  ].filter(Boolean).join(' ');

  const style = heroSlot
    ? { display: 'block', height: '100%', width: 'auto', overflow: 'visible' }
    : size === '100%'
      ? { display: 'block', width: '100%', height: '100%', overflow: 'visible' }
      : { display: 'block', width: size, height: size, overflow: 'visible' };

  useEffect(() => {
    if (!sweep) return undefined;
    if (!wait) {
      setPulsing(true);
      return () => window.clearTimeout(gapTimerRef.current);
    }
    gapTimerRef.current = window.setTimeout(() => {
      setIntro(false);
      setPulsing(true);
    }, 950);
    return () => window.clearTimeout(gapTimerRef.current);
  }, [sweep, wait]);

  useEffect(() => {
    if (!pulsing || !svgRef.current) return undefined;
    const durMs = wait ? null : nextShimmerDurMs();
    svgRef.current.querySelectorAll('animate').forEach((node) => {
      if (durMs != null) node.setAttribute('dur', `${durMs}ms`);
      rerollWingLight(node);
      if (typeof node.beginElement === 'function') node.beginElement();
    });
    if (wait) return undefined;
    const pulseTimer = window.setTimeout(() => {
      setPulsing(false);
      gapTimerRef.current = window.setTimeout(() => setPulsing(true), nextShimmerGapMs());
    }, durMs);
    return () => window.clearTimeout(pulseTimer);
  }, [pulsing, wait]);

  const handleAnimationEnd = (event) => {
    if (event.animationName === 'fusionMsiWaitHaloIn') {
      setIntro(false);
      return;
    }
    if (event.animationName !== 'fusionMsiWaitHaloPulse') return;
    setPulsing(false);
    window.clearTimeout(gapTimerRef.current);
    gapTimerRef.current = window.setTimeout(() => setPulsing(true), nextPulseGapMs());
  };

  return (
    <svg
      ref={svgRef}
      className={rootClass}
      data-aperture-variant="aperture-lab-flash14"
      data-fusion-msi-hero-mark={heroSlot ? '' : undefined}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1000 1120"
      role={decorative ? 'presentation' : 'img'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
      style={style}
      onAnimationEnd={handleAnimationEnd}
    >
      <defs>
        <BlueSatinGradient id={`${uid}-blue-satin-upper`} x1="8%" y1="82%" x2="92%" y2="12%" sweep={sweep} />
        <BlueSatinGradient id={`${uid}-blue-satin-lower`} x1="8%" y1="18%" x2="92%" y2="88%" sweep={sweep} />
        <linearGradient id={`${uid}-blue-gloss-upper`} x1="12%" y1="86%" x2="88%" y2="10%">
          <stop offset="0%" stopColor="#DCE7F0" stopOpacity="0" />
          <stop offset="52%" stopColor="#3F6F9F" stopOpacity="0.14" />
          <stop offset="76%" stopColor="#DCE7F0" stopOpacity="0.5" />
          <stop offset="88%" stopColor="#9FB9D0" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#183A6C" stopOpacity="0" />
          <LightSweep active={sweep} xValues="-30%;88%;0%;90%;-30%" yValues="100%;8%;70%;6%;100%" />
        </linearGradient>
        <linearGradient id={`${uid}-blue-gloss-lower`} x1="12%" y1="14%" x2="88%" y2="90%">
          <stop offset="0%" stopColor="#DCE7F0" stopOpacity="0" />
          <stop offset="52%" stopColor="#3F6F9F" stopOpacity="0.14" />
          <stop offset="76%" stopColor="#DCE7F0" stopOpacity="0.5" />
          <stop offset="88%" stopColor="#9FB9D0" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#183A6C" stopOpacity="0" />
          <LightSweep active={sweep} xValues="10%;92%;20%;88%;10%" yValues="0%;92%;30%;94%;0%" />
        </linearGradient>
        <linearGradient id={`${uid}-gold-upper`} x1="94%" y1="78%" x2="8%" y2="12%">
          <stop offset="0%" stopColor="#684817" />
          <stop offset="18%" stopColor="#806020" />
          <stop offset="40%" stopColor="#A17A2B" />
          <stop offset="60%" stopColor="#C09A42" />
          <stop offset="76%" stopColor="#D8BA6C" />
          <stop offset="88%" stopColor="#F0DBA0" />
          <stop offset="95%" stopColor="#FFF0C5" />
          <stop offset="100%" stopColor="#A47B29" />
          <LightSweep active={sweep} xValues="130%;18%;108%;22%;130%" yValues="0%;84%;18%;88%;0%" />
        </linearGradient>
        <linearGradient id={`${uid}-gold-lower`} x1="94%" y1="22%" x2="8%" y2="88%">
          <stop offset="0%" stopColor="#684817" />
          <stop offset="18%" stopColor="#806020" />
          <stop offset="40%" stopColor="#A17A2B" />
          <stop offset="60%" stopColor="#C09A42" />
          <stop offset="76%" stopColor="#D8BA6C" />
          <stop offset="88%" stopColor="#F0DBA0" />
          <stop offset="95%" stopColor="#FFF0C5" />
          <stop offset="100%" stopColor="#A47B29" />
          <LightSweep active={sweep} xValues="-10%;120%;40%;115%;-10%" yValues="100%;12%;78%;8%;100%" />
        </linearGradient>
        <linearGradient id={`${uid}-gold-gloss-upper`} x1="88%" y1="86%" x2="12%" y2="10%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="48%" stopColor="#D7BC75" stopOpacity="0.13" />
          <stop offset="72%" stopColor="#F4E2AD" stopOpacity="0.76" />
          <stop offset="87%" stopColor="#FFF9DF" stopOpacity="0.42" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          <LightSweep active={sweep} xValues="120%;8%;100%;12%;120%" yValues="0%;90%;22%;86%;0%" />
        </linearGradient>
        <linearGradient id={`${uid}-gold-gloss-lower`} x1="88%" y1="14%" x2="12%" y2="90%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="48%" stopColor="#D7BC75" stopOpacity="0.13" />
          <stop offset="72%" stopColor="#F4E2AD" stopOpacity="0.76" />
          <stop offset="87%" stopColor="#FFF9DF" stopOpacity="0.42" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          <LightSweep active={sweep} xValues="10%;115%;30%;108%;10%" yValues="100%;6%;80%;10%;100%" />
        </linearGradient>
        <radialGradient id={`${uid}-center-pearl`} cx="46%" cy="42%" r="68%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="42%" stopColor="#FFFFFF" />
          <stop offset="68%" stopColor="#F4F7FA" />
          <stop offset="86%" stopColor="#DCE5ED" />
          <stop offset="100%" stopColor="#AEBFCC" />
        </radialGradient>
        <linearGradient id={`${uid}-center-highlight`} x1="20%" y1="15%" x2="78%" y2="88%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="35%" stopColor="#FFFFFF" stopOpacity="0.35" />
          <stop offset="58%" stopColor="#DDE8F0" stopOpacity="0.12" />
          <stop offset="76%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={`${uid}-halo`} cx="500" cy="560" r="420" gradientUnits="userSpaceOnUse">
          <stop offset="0.45" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          {travel ? (
            <>
              <animate attributeName="cx" values="380;500;640;500;380" {...WAIT_LIGHT_TIMING} />
              <animate attributeName="cy" values="470;560;650;560;470" {...WAIT_LIGHT_TIMING} />
            </>
          ) : null}
        </radialGradient>
      </defs>

      {shimmer ? null : (
        <circle className="aperture-lab__halo" cx="500" cy="560" r="300" fill={`url(#${uid}-halo)`} />
      )}

      <g className="aperture-lab__half aperture-lab__half--left">
        <path
          d="M72 560 C188 533 300 468 382 368 C438 299 470 206 492 75 C494 59 495 43 496 27 L496 387 C496 458 478 503 437 529 C373 551 258 559 72 560 Z"
          fill={`url(#${uid}-blue-satin-upper)`}
        />
        <path
          d="M72 560 C188 587 300 652 382 752 C438 821 470 914 492 1045 C494 1061 495 1077 496 1093 L496 733 C496 662 478 617 437 591 C373 569 258 561 72 560 Z"
          fill={`url(#${uid}-blue-satin-lower)`}
        />
        <path
          d="M91 560 C225 532 328 469 397 381 C447 317 476 237 490 148 L490 401 C488 462 469 497 430 518 C357 543 246 555 91 560 Z"
          fill="#0F2B55"
          opacity="0.4"
        />
        <path
          d="M91 560 C225 588 328 651 397 739 C447 803 476 883 490 972 L490 719 C488 658 469 623 430 602 C357 577 246 565 91 560 Z"
          fill="#0F2B55"
          opacity="0.4"
        />
        <path
          className="aperture-lab__gloss"
          d="M115 542 C242 504 338 442 401 353 C444 293 470 226 486 159 C465 271 428 361 373 426 C306 505 214 536 115 542 Z"
          fill={`url(#${uid}-blue-gloss-upper)`}
        />
        <path
          className="aperture-lab__gloss"
          d="M115 578 C242 616 338 678 401 767 C444 827 470 894 486 961 C465 849 428 759 373 694 C306 615 214 584 115 578 Z"
          fill={`url(#${uid}-blue-gloss-lower)`}
        />
      </g>

      <g className="aperture-lab__half aperture-lab__half--right">
        <path
          d="M928 560 C812 533 700 468 618 368 C562 299 530 206 508 75 C506 59 505 43 504 27 L504 387 C504 458 522 503 563 529 C627 551 742 559 928 560 Z"
          fill={`url(#${uid}-gold-upper)`}
        />
        <path
          d="M928 560 C812 587 700 652 618 752 C562 821 530 914 508 1045 C506 1061 505 1077 504 1093 L504 733 C504 662 522 617 563 591 C627 569 742 561 928 560 Z"
          fill={`url(#${uid}-gold-lower)`}
        />
        <path
          d="M909 560 C775 532 672 469 603 381 C553 317 524 237 510 148 L510 401 C512 462 531 497 570 518 C643 543 754 555 909 560 Z"
          fill="#76551C"
          opacity="0.46"
        />
        <path
          d="M909 560 C775 588 672 651 603 739 C553 803 524 883 510 972 L510 719 C512 658 531 623 570 602 C643 577 754 565 909 560 Z"
          fill="#76551C"
          opacity="0.46"
        />
        <path
          className="aperture-lab__gloss"
          d="M885 542 C758 504 662 442 599 353 C556 293 530 226 514 159 C535 271 572 361 627 426 C694 505 786 536 885 542 Z"
          fill={`url(#${uid}-gold-gloss-upper)`}
        />
        <path
          className="aperture-lab__gloss"
          d="M885 578 C758 616 662 678 599 767 C556 827 530 894 514 961 C535 849 572 759 627 694 C694 615 786 584 885 578 Z"
          fill={`url(#${uid}-gold-gloss-lower)`}
        />
      </g>

      <g className="aperture-lab__core">
        <path
          d="M500 356 C506 460 514 520 535 542 C556 555 606 558 687 560 C606 562 556 565 535 578 C514 600 506 660 500 764 C494 660 486 600 465 578 C444 565 394 562 313 560 C394 558 444 555 465 542 C486 520 494 460 500 356 Z"
          fill={`url(#${uid}-center-pearl)`}
          stroke="#AABBC9"
          strokeOpacity="0.34"
          strokeWidth="1.8"
        />
        <path
          d="M500 391 C505 474 515 522 537 543 C558 556 597 559 656 560 C597 561 558 564 537 577 C515 598 505 646 500 729 C495 646 485 598 463 577 C442 564 403 561 344 560 C403 559 442 556 463 543 C485 522 495 474 500 391 Z"
          fill={`url(#${uid}-center-highlight)`}
          opacity="0.62"
        />
        <ellipse className="aperture-lab__spark" cx="500" cy="560" rx="22" ry="19" fill="#FFFFFF" opacity="0.94" />
      </g>
    </svg>
  );
}

Flash14StarMark.propTypes = {
  animated: PropTypes.bool,
  ascent: PropTypes.bool,
  wait: PropTypes.bool,
  shimmer: PropTypes.bool,
  decorative: PropTypes.bool,
  heroSlot: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['100%'])]),
};

export default Flash14StarMark;
